/**
 * Field upload queue — localStorage-backed retry queue for resilience under
 * weak reception. Specifically designed for crew photo uploads & variations.
 *
 * Strategy:
 *  - Each enqueued item lives as { id, kind, payload, attempts, lastError, createdAt }
 *  - Files (Blob) are persisted via IndexedDB (single store "files" keyed by id)
 *  - JSON payloads + file metadata live in localStorage under KEY
 *  - On reconnect / on app start, flush() drains the queue with retry/backoff.
 *  - subscribe() lets UI re-render when queue state changes (size / status).
 *
 * Public API:
 *   queue.enqueueUpload({ projectId, category, file, linkedKind, linkedId, caption })
 *   queue.enqueueVariation({ payload })
 *   queue.snapshot() -> { items, pending, failed, syncing, online }
 *   queue.subscribe(fn) -> unsubscribe
 *   queue.flush() (manual trigger)
 */
import api from "./api";

const KEY = "mr.ops.upload_queue.v1";
const DB_NAME = "mr-ops-uploads";
const STORE = "files";

// ---------------- IndexedDB helpers ----------------
let _dbPromise = null;
function openDb() {
  if (_dbPromise) return _dbPromise;
  _dbPromise = new Promise((resolve, reject) => {
    if (typeof indexedDB === "undefined") return resolve(null);
    const req = indexedDB.open(DB_NAME, 1);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) db.createObjectStore(STORE);
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
  return _dbPromise;
}
async function dbPutFile(id, blob) {
  const db = await openDb();
  if (!db) return false;
  return new Promise((res, rej) => {
    const tx = db.transaction(STORE, "readwrite");
    tx.objectStore(STORE).put(blob, id);
    tx.oncomplete = () => res(true);
    tx.onerror = () => rej(tx.error);
  });
}
async function dbGetFile(id) {
  const db = await openDb();
  if (!db) return null;
  return new Promise((res, rej) => {
    const tx = db.transaction(STORE, "readonly");
    const r = tx.objectStore(STORE).get(id);
    r.onsuccess = () => res(r.result || null);
    r.onerror = () => rej(r.error);
  });
}
async function dbDeleteFile(id) {
  const db = await openDb();
  if (!db) return;
  const tx = db.transaction(STORE, "readwrite");
  tx.objectStore(STORE).delete(id);
}

// ---------------- queue state ----------------
function load() {
  try {
    const raw = JSON.parse(localStorage.getItem(KEY) || "[]");
    if (!Array.isArray(raw)) throw new Error("not an array");
    // Quarantine malformed records — keep only items with required shape
    const clean = [];
    const quarantined = [];
    for (const item of raw) {
      if (item && typeof item === "object" && item.id && item.kind && item.payload) {
        clean.push(item);
      } else {
        quarantined.push(item);
      }
    }
    if (quarantined.length > 0) {
      try {
        const prevQ = JSON.parse(localStorage.getItem(KEY + ".quarantine") || "[]");
        localStorage.setItem(KEY + ".quarantine", JSON.stringify([...prevQ, ...quarantined].slice(-50)));
      } catch {/* ignore */}
      localStorage.setItem(KEY, JSON.stringify(clean));
    }
    return clean;
  } catch {
    // Corrupted storage — quarantine the raw blob and start fresh
    try {
      const raw = localStorage.getItem(KEY);
      if (raw) localStorage.setItem(KEY + ".corrupt", raw);
    } catch {/* ignore */}
    localStorage.setItem(KEY, "[]");
    return [];
  }
}
function save(items) {
  localStorage.setItem(KEY, JSON.stringify(items));
  _emit();
}

const listeners = new Set();
let _flushing = false;
let _lastError = null;

function snapshot() {
  const items = load();
  return {
    items,
    pending: items.filter((i) => !i.lastError).length,
    failed: items.filter((i) => i.lastError && (i.attempts || 0) >= 3).length,
    syncing: _flushing,
    online: typeof navigator === "undefined" || navigator.onLine !== false,
    lastError: _lastError,
  };
}

function subscribe(fn) {
  listeners.add(fn);
  fn(snapshot());
  return () => listeners.delete(fn);
}

function _emit() {
  const s = snapshot();
  listeners.forEach((fn) => { try { fn(s); } catch {/* ignore */} });
}

// ---------------- enqueue helpers ----------------
let _idCounter = 0;
function _newId() {
  _idCounter += 1;
  return `q_${Date.now()}_${_idCounter}`;
}

async function enqueueUpload({ projectId, category, file, linkedKind, linkedId, caption }) {
  if (!projectId || !file) throw new Error("projectId + file required");
  const id = _newId();
  await dbPutFile(id, file);
  const items = load();
  items.push({
    id,
    kind: "upload",
    payload: {
      projectId, category, linkedKind, linkedId, caption,
      filename: file.name, size: file.size, type: file.type,
    },
    attempts: 0,
    createdAt: Date.now(),
  });
  save(items);
  _postTelemetry({
    event: "queued", queue_item_id: id, kind: "upload",
    filename: file.name, size_bytes: file.size,
  });
  if (snapshot().online) flush();
  return id;
}

async function enqueueVariation({ payload }) {
  const id = _newId();
  const items = load();
  items.push({
    id, kind: "variation", payload,
    attempts: 0, createdAt: Date.now(),
  });
  save(items);
  if (snapshot().online) flush();
  return id;
}

async function enqueueCompletion({ payload }) {
  const id = _newId();
  const items = load();
  items.push({
    id, kind: "completion", payload,
    attempts: 0, createdAt: Date.now(),
  });
  save(items);
  if (snapshot().online) flush();
  return id;
}

function remove(id) {
  save(load().filter((i) => i.id !== id));
  dbDeleteFile(id);
}

// ---------------- flush / replay ----------------
async function _send(item) {
  if (item.kind === "upload") {
    const blob = await dbGetFile(item.id);
    if (!blob) throw new Error("File missing from local storage");
    const fd = new FormData();
    fd.append("project_id", item.payload.projectId);
    fd.append("category", item.payload.category || "other");
    if (item.payload.caption) fd.append("caption", item.payload.caption);
    if (item.payload.linkedKind) fd.append("linked_kind", item.payload.linkedKind);
    if (item.payload.linkedId) fd.append("linked_id", item.payload.linkedId);
    fd.append("file", blob, item.payload.filename || "upload.bin");
    await api.post("/api/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
  } else if (item.kind === "variation") {
    await api.post("/api/field/variations", item.payload);
  } else if (item.kind === "completion") {
    await api.post("/api/field/completions", item.payload);
  }
}

async function flush() {
  if (_flushing) return;
  if (!snapshot().online) return;
  _flushing = true;
  _emit();
  try {
    let items = load();
    for (const item of items) {
      if ((item.attempts || 0) >= 3 && item.lastError) continue;
      try {
        await _send(item);
        // success — remove from queue
        items = load().filter((i) => i.id !== item.id);
        save(items);
        if (item.kind === "upload") await dbDeleteFile(item.id);
        _lastError = null;
        _postTelemetry({
          event: "succeeded",
          queue_item_id: item.id,
          kind: item.kind,
          filename: item.payload?.filename,
          size_bytes: item.payload?.size,
          attempts: (item.attempts || 0) + 1,
        });
      } catch (e) {
        items = load();
        const idx = items.findIndex((i) => i.id === item.id);
        if (idx >= 0) {
          items[idx].attempts = (items[idx].attempts || 0) + 1;
          items[idx].lastError = (e && (e.response?.data?.detail || e.message)) || "error";
          save(items);
          // If we've reached the retry cap, report failure once
          if (items[idx].attempts >= 3) {
            _postTelemetry({
              event: "failed",
              queue_item_id: item.id,
              kind: item.kind,
              filename: item.payload?.filename,
              size_bytes: item.payload?.size,
              attempts: items[idx].attempts,
              error: items[idx].lastError,
            });
          }
        }
        _lastError = items[idx]?.lastError || "error";
      }
    }
  } finally {
    _flushing = false;
    _emit();
  }
}

async function _postTelemetry(event) {
  try { await api.post("/api/ops/telemetry/upload", event); } catch {/* best-effort */}
}

// ---------------- auto-replay on reconnect ----------------
if (typeof window !== "undefined") {
  window.addEventListener("online", () => { flush(); });
  // Periodic safety net every 60s
  setInterval(() => { if (snapshot().online && load().length > 0) flush(); }, 60_000);
}

const queue = {
  enqueueUpload,
  enqueueVariation,
  enqueueCompletion,
  remove,
  snapshot,
  subscribe,
  flush,
};
export default queue;
